import React, { createContext, useState } from "react";
import { ShowToast, Severty } from "../helper/toast";
import { forumsAPI } from "../services/api";

export const ForumsContext = createContext();

export const ForumsProvider = ({ children }) => {
  const [forums, setForums] = useState([]);
  const [isLoading, setLoading] = useState(false);

  const getForums = async (params) => {
    try {
      setLoading(true);
      const response = await forumsAPI.getForums(params);
      if (response?.status === 200 && response?.data) {
        setForums(response.data.forums || []);
        return { success: true, data: response.data };
      } else {
        console.warn("Failed to load forums:", response?.message);
        return { success: false, error: response?.message };
      }
    } catch (error) {
      const errorMessage =
        error?.message ||
        error?.error ||
        "Failed to fetch forums. Please try again.";
      ShowToast(errorMessage, Severty.ERROR);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const getForumById = async (forumId, query = {}) => {
    if (!forumId) {
      ShowToast("Forum ID is required", Severty.ERROR);
      return { success: false, error: "Forum ID is required" };
    }

    try {
      setLoading(true);
      const response = await forumsAPI.getForumThreads(forumId, query);
      if (response?.status === 200 && response?.data) {
        return { success: true, data: response.data };
      } else {
        console.warn("Failed to load forum threads:", response?.message);
        return { success: false, error: response?.message };
      }
    } catch (error) {
      const errorMessage =
        error?.message ||
        error?.error ||
        "Failed to fetch forum threads. Please try again.";
      ShowToast(errorMessage, Severty.ERROR);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const createThread = async (data) => {
    try {
      setLoading(true);
      const response = await forumsAPI.createThread(data);
      if (
        (response?.status === 200 || response?.status === 201) &&
        response?.data
      ) {
        ShowToast("Thread created successfully!", Severty.SUCCESS);
        return { success: true, data: response.data };
      } else {
        console.warn("Failed to create thread:", response?.message);
        ShowToast(
          response?.message || "Failed to create thread",
          Severty.ERROR
        );
        return { success: false, error: response?.message };
      }
    } catch (error) {
      const errorMessage =
        error?.message ||
        error?.error ||
        "Failed to create thread. Please try again.";
      ShowToast(errorMessage, Severty.ERROR);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  // Add this function inside ForumsProvider

  const getThreadDetails = async (threadId) => {
    if (!threadId) {
      ShowToast("Thread ID is required", Severty.ERROR);
      return { success: false, error: "Thread ID is required" };
    }

    try {
      setLoading(true);
      const response = await forumsAPI.getThreadDetails(threadId); // Make sure this API exists in your services/api
      if (response?.status === 200 && response?.data) {
        return { success: true, data: response.data };
      } else {
        console.warn("Failed to load thread details:", response?.message);
        return { success: false, error: response?.message };
      }
    } catch (error) {
      const errorMessage =
        error?.message ||
        error?.error ||
        "Failed to fetch thread details. Please try again.";
      ShowToast(errorMessage, Severty.ERROR);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  const replyOnThread = async (threadId, formData, config = {}) => {
    if (!threadId) {
      ShowToast("Thread ID is required", Severty.ERROR);
      return { success: false, error: "Thread ID is required" };
    }

    try {
      setLoading(true);
      const response = await forumsAPI.replyOnThread(
        threadId,
        formData,
        config
      );
      if (
        (response?.status === 200 || response?.status === 201) &&
        response?.data
      ) {
        ShowToast("Reply posted successfully!", Severty.SUCCESS);
        return { success: true, data: response.data };
      } else {
        console.warn("Failed to reply on thread:", response?.message);
        ShowToast(
          response?.message || "Failed to reply on thread",
          Severty.ERROR
        );
        return { success: false, error: response?.message };
      }
    } catch (error) {
      const errorMessage =
        error?.message ||
        error?.error ||
        "Failed to reply on thread. Please try again.";
      ShowToast(errorMessage, Severty.ERROR);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  // ✅ Reply on comment
  const replyOnComment = async (threadId, commentId, formData, config = {}) => {
    if (!threadId || !commentId) {
      ShowToast("Thread ID and Comment ID are required", Severty.ERROR);
      return { success: false, error: "Thread ID and Comment ID are required" };
    }

    try {
      setLoading(true);
      const response = await forumsAPI.replyOnComment(
        threadId,
        commentId,
        formData,
        config
      );
      if (
        (response?.status === 200 || response?.status === 201) &&
        response?.data
      ) {
        ShowToast("Reply posted successfully!", Severty.SUCCESS);
        return { success: true, data: response.data };
      } else {
        console.warn("Failed to reply on comment:", response?.message);
        ShowToast(
          response?.message || "Failed to reply on comment",
          Severty.ERROR
        );
        return { success: false, error: response?.message };
      }
    } catch (error) {
      const errorMessage =
        error?.message ||
        error?.error ||
        "Failed to reply on comment. Please try again.";
      ShowToast(errorMessage, Severty.ERROR);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  // inside ForumsProvider
  const submitVote = async (threadId, commentId = null, action) => {
    if (!threadId || !action) {
      ShowToast("Thread ID and action are required", Severty.ERROR);
      return { success: false, error: "Thread ID and action are required" };
    }

    try {
      setLoading(true);
      const body = { commentId, action };

      const response = await forumsAPI.submitVote(threadId, body);
      if (response?.status === 200 && response?.data) {
        // ShowToast("Vote submitted successfully!", Severty.SUCCESS);
        return response;
      } else {
        ShowToast(response?.message || "Failed to submit vote", Severty.ERROR);
        return { success: false, error: response?.message };
      }
    } catch (error) {
      const errorMessage =
        error?.message || "Failed to submit vote. Please try again.";
      ShowToast(errorMessage, Severty.ERROR);
      return { success: false, error: errorMessage };
    } finally {
      setLoading(false);
    }
  };

  // Report Forums
  const reportForum = async (threadId, data) => {
    try {
      const response = await forumsAPI.reportForum(threadId, data);
      return response;
    } catch (error) {
      throw error;
    }
  };

  return (
    <ForumsContext.Provider
      value={{
        forums,
        isLoading,
        // Actions
        getForums,
        getForumById,
        createThread,
        getThreadDetails,
        replyOnThread, // ✅ added
        replyOnComment, // ✅ added
        submitVote,
        // Setters
        setForums,
        setLoading,
        reportForum,
      }}
    >
      {children}
    </ForumsContext.Provider>
  );
};

// Custom hook
export const useForums = () => {
  const context = React.useContext(ForumsContext);
  if (context === undefined) {
    throw new Error("useForums must be used within a ForumsProvider");
  }
  return context;
};
